import { FilterType } from '@/models/filter/FilterType';
import { collapseSingleChildLeaves } from '@/models/filter/utils/collapseSingleChildLeaves';
import { extractCountryName } from '@/models/filter/utils/countryFilterHelpers';
import type { FilterState } from '@/types/filter/filterState';
import type { SelectItem } from '@/types/ui/selectItem';

export const EXTERNAL_FILTER_PARAM = 'externalFilter';

export const EXTERNAL_FILTER_KEYS: readonly FilterType[] = [
  FilterType.COUNTRY,
  FilterType.INDICATION,
  FilterType.PRODUCTS,
  FilterType.CLASSIFICATION_LEVELS,
];

export interface ExternalFilterParams {
  filters: Partial<FilterState>;
  consumedKeys: FilterType[];
}

export interface ResolvedExternalCountry {
  selectedPaths: string[];
  countryCodes: string[];
  unavailableCountryCodes: string[];
}

const isExternalFilterKey = (key: string): key is FilterType =>
  EXTERNAL_FILTER_KEYS.some(filterKey => filterKey === key);

const parseValues = (values: string[]): string[] => [
  ...new Set(
    values.flatMap(value =>
      value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean),
    ),
  ),
];

const countryDisplayNames = new Intl.DisplayNames(['en'], { type: 'region' });

const resolveCountryName = (countryCode: string, countryNamesByCode: Map<string, string>): string | undefined => {
  const countryNameFromQuery = countryNamesByCode.get(countryCode);
  if (typeof countryNameFromQuery === 'string') return countryNameFromQuery;

  try {
    const countryNameFromIsoCode = countryDisplayNames.of(countryCode);
    return countryNameFromIsoCode === countryCode ? undefined : countryNameFromIsoCode;
  } catch {
    return undefined;
  }
};

export const parseExternalFilterParams = (searchParams: URLSearchParams): ExternalFilterParams | null => {
  if (!searchParams.has(EXTERNAL_FILTER_PARAM)) return null;

  const filters: Partial<FilterState> = {};
  const consumedKeys: FilterType[] = [];

  for (const key of searchParams.keys()) {
    if (!isExternalFilterKey(key) || consumedKeys.includes(key)) continue;

    const values = parseValues(searchParams.getAll(key));
    if (values.length === 0) continue;

    filters[key] = values;
    consumedKeys.push(key);
  }

  return { filters, consumedKeys };
};

export const buildExternalFilterName = (label: string, timestamp: Date): string =>
  `${label} ${timestamp.toISOString()}`;

export const resolveExternalCountry = (
  countryCodes: string[],
  userCountries: SelectItem<string>[],
  countryOptions: SelectItem<string>[],
): ResolvedExternalCountry => {
  const countryNamesByCode = new Map<string, string>();
  userCountries.forEach(country => {
    if (typeof country.label === 'string') {
      countryNamesByCode.set(country.value.toUpperCase(), country.label);
    }
  });
  const selectableOptions = collapseSingleChildLeaves(countryOptions);
  const nonLeafPaths = new Set<string>();

  countryOptions.forEach(option => {
    const segments = option.value.split('__');

    for (let index = 1; index < segments.length; index += 1) {
      nonLeafPaths.add(segments.slice(0, index).join('__'));
    }
  });

  const selectedPaths: string[] = [];
  const resolvedCountryCodes: string[] = [];
  const unavailableCountryCodes: string[] = [];

  for (const countryCode of countryCodes) {
    const normalizedCode = countryCode.toUpperCase();
    const countryName = resolveCountryName(normalizedCode, countryNamesByCode);
    if (typeof countryName !== 'string') {
      unavailableCountryCodes.push(countryCode);
      continue;
    }

    const matchingPaths = new Set(
      countryOptions.flatMap((option, index) => {
        if (nonLeafPaths.has(option.value) || extractCountryName(option.value) !== countryName) return [];

        return [selectableOptions[index]?.value ?? option.value];
      }),
    );

    if (matchingPaths.size === 0) {
      unavailableCountryCodes.push(countryCode);
      continue;
    }

    selectedPaths.push(...matchingPaths);
    resolvedCountryCodes.push(normalizedCode);
  }

  return { selectedPaths, countryCodes: resolvedCountryCodes, unavailableCountryCodes };
};
